import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';

function PitchTech() {
  const [, setActiveSection] = useState('');
  const [pricings, setPricings] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);

  useEffect(() => {
    // Smooth scrolling for anchor links
    const handleAnchorClick = (e) => {
      const href = e.target.getAttribute('href') || e.target.closest('a')?.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          setActiveSection(href.substring(1));
        }
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', handleAnchorClick);
    });

    // Intersection Observer for active section highlighting
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    document.querySelectorAll('section[id]').forEach(section => {
      observer.observe(section);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', handleAnchorClick);
      });
      observer.disconnect();
    };
  }, []);

  // Fetch pricing data from API
  useEffect(() => {
    const fetchPricings = async () => {
      try {
        const response = await api.get('/pricing');
        if (response.data.success && Array.isArray(response.data.data)) {
          setPricings(response.data.data);
        } else {
          setPricings([]);
        }
      } catch (error) {
        console.error('Error fetching pricing plans:', error);
        setPricings([]);
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricings();
  }, []);

  const keyFeatures = [
    {
      icon: 'fas fa-project-diagram',
      title: 'Project Management',
      description: 'Create, assign, and manage projects with ease. Track time, set milestones, and monitor progress.',
      color: '#4A90E2'
    },
    {
      icon: 'fas fa-file-invoice-dollar',
      title: 'Invoicing & Billing',
      description: 'Generate professional invoices, support recurring billing, and accept online payments.',
      color: '#48BB78'
    },
    {
      icon: 'fas fa-file-alt',
      title: 'Proposal Builder',
      description: 'Create stunning proposals with images, videos, and e-signature support.',
      color: '#4A90E2'
    },
    {
      icon: 'fas fa-users',
      title: 'Lead Management',
      description: 'Kanban-style pipeline, auto-import leads, and convert to customers seamlessly.',
      color: '#48BB78'
    },
    {
      icon: 'fas fa-money-bill-wave',
      title: 'Expenses & Payments',
      description: 'Track expenses, set recurring payments, and manage financial records.',
      color: '#4A90E2'
    },
    {
      icon: 'fas fa-headset',
      title: 'Support System',
      description: 'Integrated ticketing with pre-defined replies and internal collaboration.',
      color: '#48BB78'
    }
  ];

  const benefits = [
    {
      icon: 'fas fa-dollar-sign',
      title: 'Save 50% or More',
      description: 'Dramatically reduce CRM costs compared to traditional solutions',
      stat: '50%',
      color: '#48BB78'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Secure & Compliant',
      description: 'HTTPS enforcement, GDPR-ready, and complete data ownership',
      stat: '100%',
      color: '#4A90E2'
    },
    {
      icon: 'fas fa-bolt',
      title: 'Ready to Use',
      description: 'Pre-configured for SMEs with ready-to-use forms and reports',
      stat: '0 Days',
      color: '#48BB78'
    },
    {
      icon: 'fas fa-infinity',
      title: 'Unlimited Scale',
      description: 'Support unlimited users, clients, and projects',
      stat: '∞',
      color: '#4A90E2'
    }
  ];

  const testimonials = [
    {
      quote: "AcuIT CRM is manageable and has made a significant impact on our business bottom line. The lead tracking alone has improved our conversion rate by 40%.",
      author: "Jeganesh",
      company: "Esram Metals Sdn Bhd",
      image: "/assets/images/clients/1.png"
    },
    {
      quote: "AcuIT CRM has helped us to manage, track and do more business with ease. I'm confident that we are now following up with each lead at the right time.",
      author: "Vengkata Rao",
      company: "Winas ICT Sdn Bhd",
      image: "/assets/images/clients/2.png"
    }
  ];

  return (
    <div className="pitch-tech-page" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      {/* Hero Section */}
      <section className="hero-pitch rel z-2 pt-235 rpt-150 pb-130 rpb-100" style={{
        background: 'linear-gradient(135deg, #4A90E2 0%, #48BB78 100%)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '150px',
        paddingBottom: '80px'
      }}>
        <div className="container" style={{paddingTop: '20px'}}>
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-11">
              <div className="hero-content-pitch rpt-15 rmb-75 text-white" style={{
                paddingTop: '40px',
                maxWidth: '100%'
              }}>
                <div className="mb-4" style={{
                  fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                  fontWeight: 'bold',
                  opacity: 0.9,
                  lineHeight: '1.2'
                }}>
                  Acuit CRM
                </div>
                <h1 className="mb-4" style={{
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  fontWeight: 'bold',
                  lineHeight: '1.3',
                  marginBottom: '20px'
                }}>
                  Advanced Cloud CRM Software Solutions
                </h1>
                <p className="mb-5" style={{
                  fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                  opacity: 0.95,
                  lineHeight: '1.8',
                  marginBottom: '35px',
                  maxWidth: '100%'
                }}>
                  Empower your business with best-in-class CRM software in Malaysia. Our CRM Software (Customer Relationship Management) will help you turn conversations into conversions at every stage of a customer's journey.
                </p>
                <div className="d-flex flex-wrap align-items-center" style={{
                  gap: '15px',
                  marginTop: '30px'
                }}>
                  <Link to="/contact" className="theme-btn style-two" style={{
                    background: 'white',
                    color: '#4A90E2',
                    padding: '14px 32px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s',
                    fontSize: '16px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                  }}>
                    Schedule a Demo <i className="fas fa-arrow-right ml-2"></i>
                  </Link>
                  <a href="#solution" className="theme-btn style-three" style={{
                    background: 'transparent',
                    color: 'white',
                    border: '2px solid white',
                    padding: '14px 32px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s',
                    fontSize: '16px',
                    whiteSpace: 'nowrap'
                  }}>
                    Learn More <i className="fas fa-arrow-right ml-2"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-pitch text-center">
                <img src="/my/h1.jpeg" alt="Acuit CRM Dashboard" style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '15px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }} />
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <img className="dots-shape" src="/assets/images/shapes/dots.png" alt="Shape" style={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            opacity: 0.1
          }} />
          <img className="tringle-shape" src="/assets/images/shapes/tringle.png" alt="Shape" style={{
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            opacity: 0.1
          }} />
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-130 rpt-100 rpb-100 bg-lighter" id="problem">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 text-center mb-60">
              <div className="section-title">
                <span className="sub-title" style={{color: '#4A90E2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px'}}>The Challenge</span>
                <h2 className="mt-3 mb-4">The Challenge Malaysian SMEs Face</h2>
                <p className="text-muted">Without proper CRM systems, businesses are losing valuable opportunities every day</p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6 mb-4">
              <div className="solution-item-two" style={{
                background: 'white',
                padding: '40px',
                borderRadius: '15px',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #F56565',
                height: '100%',
                transition: 'transform 0.3s'
              }}>
                <h4 className="mb-4" style={{color: '#F56565', fontSize: '24px', fontWeight: 'bold'}}>
                  <i className="fas fa-exclamation-triangle mr-2"></i>Common Pain Points
                </h4>
                <ul className="list-style-one">
                  <li className="mb-3">Fragmented sales processes with no centralized tracking</li>
                  <li className="mb-3">Poor lead follow-up resulting in lost opportunities</li>
                  <li className="mb-3">Low conversion rates due to manual tracking</li>
                  <li className="mb-3">No holistic view of customer journey</li>
                  <li className="mb-3">High CRM costs and lengthy setup times</li>
                </ul>
              </div>
            </div>
            <div className="col-lg-6 mb-4">
              <div className="solution-item-two color-two" style={{
                background: 'linear-gradient(135deg, #EBF8FF 0%, #F0FFF4 100%)',
                padding: '40px',
                borderRadius: '15px',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #4A90E2',
                height: '100%'
              }}>
                <h4 className="mb-4" style={{color: '#4A90E2', fontSize: '24px', fontWeight: 'bold'}}>
                  <i className="fas fa-chart-line mr-2"></i>The Impact
                </h4>
                <p className="mb-3" style={{fontSize: '18px', lineHeight: '1.8'}}>
                  Without proper CRM systems, Malaysian SMEs are losing valuable opportunities every day.
                </p>
                <p className="mb-3" style={{fontSize: '18px', lineHeight: '1.8'}}>
                  The result? Decreased revenue, frustrated sales teams, and missed growth potential.
                </p>
                <p className="mb-0" style={{fontSize: '20px', fontWeight: 'bold', color: '#1C2938'}}>
                  It's time for a change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-130 rpt-100 rpb-100" id="solution" style={{background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)'}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 text-center mb-60">
              <div className="section-title">
                <span className="sub-title mb-3 d-inline-block" style={{
                  color: '#4A90E2',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '14px'
                }}>
                  The Solution
                </span>
                <h2 className="mt-3 mb-4" style={{
                  fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                  fontWeight: 'bold',
                  color: '#1C2938',
                  lineHeight: '1.3'
                }}>
                  Introducing Acuit CRM
                </h2>
                <p className="text-muted mx-auto" style={{
                  fontSize: '18px',
                  lineHeight: '1.8',
                  maxWidth: '900px',
                  color: '#718096'
                }}>
                  All-in-one cloud CRM built specifically for Malaysian SMEs. A robust, self-hosted Customer Relationship Management system designed to empower small businesses, freelancers, startups, and growing agencies.
                </p>
              </div>
            </div>
          </div>

          <div className="row justify-content-center mb-5">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <div className="service-item wow fadeInUp h-100" style={{
                  background: 'white',
                  padding: '35px 30px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  height: '100%',
                  transition: 'all 0.3s',
                  borderTop: `4px solid ${feature.color}`,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: `${feature.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    flexShrink: 0
                  }}>
                    <i className={feature.icon} style={{fontSize: '28px', color: feature.color}}></i>
                  </div>
                  <h3 className="mb-3" style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#1C2938',
                    lineHeight: '1.3',
                    minHeight: '56px'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    color: '#718096',
                    lineHeight: '1.7',
                    fontSize: '15px',
                    marginBottom: 0,
                    flexGrow: 1
                  }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="row mt-5">
            <div className="col-12">
              <div className="card" style={{
                background: 'white',
                borderRadius: '20px',
                padding: '50px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0'
              }}>
                <div className="text-center mb-5">
                  <h3 className="mb-3" style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: '#1C2938'
                  }}>
                    Complete Business Management Suite
                  </h3>
                  <p className="mb-0" style={{
                    fontSize: '18px',
                    color: '#718096',
                    lineHeight: '1.6'
                  }}>
                    Everything you need to manage your customer relationships, sales operations, and business growth
                  </p>
                </div>
                
                <div className="row mt-4">
                  {[
                    { icon: 'fas fa-user-plus', title: 'Lead Capture', desc: 'Multi-channel lead intake', color: '#4A90E2' },
                    { icon: 'fas fa-address-book', title: 'Customer Records', desc: 'Centralized data hub', color: '#48BB78' },
                    { icon: 'fas fa-file-invoice-dollar', title: 'Billing System', desc: 'Automated invoicing', color: '#4A90E2' },
                    { icon: 'fas fa-chart-bar', title: 'Analytics', desc: 'AI-powered insights', color: '#48BB78' },
                    { icon: 'fas fa-project-diagram', title: 'Project Management', desc: 'Track & manage projects', color: '#4A90E2' },
                    { icon: 'fas fa-headset', title: 'Support Tickets', desc: 'Customer support system', color: '#48BB78' }
                  ].map((item, idx) => (
                    <div key={idx} className="col-lg-4 col-md-6 mb-4">
                      <div className="text-center" style={{
                        background: '#f8f9fa',
                        padding: '30px 25px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s',
                        height: '100%'
                      }}>
                        <i className={item.icon} style={{
                          fontSize: '32px',
                          marginBottom: '15px',
                          display: 'block',
                          color: item.color
                        }}></i>
                        <h5 className="mb-2" style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#1C2938'
                        }}>
                          {item.title}
                        </h5>
                        <p className="mb-0" style={{
                          fontSize: '14px',
                          color: '#718096',
                          lineHeight: '1.5'
                        }}>
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-5">
                  <p className="mb-4" style={{
                    fontSize: '16px',
                    color: '#718096'
                  }}>
                    <i className="fas fa-check-circle mr-2" style={{color: '#48BB78'}}></i>Fast onboarding
                    <span className="mx-3">•</span>
                    <i className="fas fa-check-circle mr-2" style={{color: '#48BB78'}}></i>No hidden costs
                    <span className="mx-3">•</span>
                    <i className="fas fa-check-circle mr-2" style={{color: '#48BB78'}}></i>Mobile access included
                  </p>
                  <Link to="/solutions" className="theme-btn" style={{
                    background: 'linear-gradient(135deg, #4A90E2 0%, #48BB78 100%)',
                    color: 'white',
                    padding: '15px 40px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-block',
                    fontSize: '16px',
                    boxShadow: '0 4px 15px rgba(74, 144, 226, 0.3)',
                    transition: 'all 0.3s'
                  }}>
                    Explore All Features <i className="fas fa-arrow-right ml-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-130 rpt-100 rpb-100 bg-blue text-white" id="value" style={{
        background: 'linear-gradient(135deg, #1C2938 0%, #2D3748 100%)',
        position: 'relative'
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 text-center mb-60">
              <div className="section-title">
                <span className="sub-title" style={{color: '#48BB78', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px'}}>Why Choose Us</span>
                <h2 className="mt-3 mb-4 text-white">Why Choose Acuit CRM?</h2>
                <p className="text-white-50" style={{fontSize: '18px'}}>
                  The all-in-one business management platform that gives you complete control
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            {benefits.map((benefit, index) => (
              <div key={index} className="col-lg-6 col-md-6 mb-4">
                <div className="solution-item-three" style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  padding: '40px',
                  borderRadius: '15px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '100%',
                  transition: 'all 0.3s'
                }}>
                  <div className="d-flex align-items-start mb-3">
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: `${benefit.color}30`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '20px',
                      flexShrink: 0
                    }}>
                      <i className={benefit.icon} style={{fontSize: '28px', color: benefit.color}}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-baseline mb-2">
                        <span className="mr-3" style={{fontSize: '48px', fontWeight: 'bold', color: benefit.color, lineHeight: '1'}}>
                          {benefit.stat}
                        </span>
                      </div>
                      <h4 className="mb-3" style={{fontSize: '24px', fontWeight: 'bold'}}>{benefit.title}</h4>
                      <p className="mb-0" style={{color: '#D1D5DB', lineHeight: '1.6'}}>{benefit.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row mt-5">
            <div className="col-12">
              <div className="text-center">
                <div className="row justify-content-center">
                  {[
                    'All-in-One Platform',
                    'Self-Hosted Control',
                    'One-Time Purchase',
                    'Unlimited Users',
                    '17 Powerful Features',
                    'GDPR Compliant'
                  ].map((item, idx) => (
                    <div key={idx} className="col-lg-4 col-md-6 mb-3">
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        padding: '15px 25px',
                        borderRadius: '25px',
                        display: 'inline-block'
                      }}>
                        <i className="fas fa-check-circle mr-2" style={{color: '#48BB78'}}></i>
                        <span>{item}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-130 rpt-100 rpb-100 bg-lighter" id="market">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 text-center mb-60">
              <div className="section-title">
                <span className="sub-title" style={{color: '#4A90E2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px'}}>Market Opportunity</span>
                <h2 className="mt-3 mb-4">Massive Market Opportunity</h2>
                <p className="text-muted">The Malaysian SME market is ready for transformation</p>
              </div>
            </div>
          </div>
          
          <div className="row mb-5">
            {[
              { number: '900K+', desc: 'Malaysian SMEs', icon: 'fas fa-building', color: '#4A90E2', bg: '#EBF8FF' },
              { number: '35%', desc: 'Cloud CRM Growth in SEA', icon: 'fas fa-chart-line', color: '#48BB78', bg: '#F0FFF4' },
              { number: 'Multi', desc: 'Target Verticals', icon: 'fas fa-layer-group', color: '#4A90E2', bg: '#EBF8FF' }
            ].map((item, idx) => (
              <div key={idx} className="col-lg-4 col-md-6 mb-4">
                <div className="text-center" style={{
                  background: 'white',
                  padding: '40px 30px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  borderTop: `4px solid ${item.color}`,
                  height: '100%'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: item.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <i className={item.icon} style={{fontSize: '36px', color: item.color}}></i>
                  </div>
                  <div className="mb-2" style={{fontSize: '48px', fontWeight: 'bold', color: item.color}}>{item.number}</div>
                  <p className="mb-0" style={{fontSize: '16px', color: '#718096', fontWeight: 500}}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="row">
            <div className="col-12">
              <div style={{
                background: 'linear-gradient(135deg, #EBF8FF 0%, #F0FFF4 100%)',
                borderRadius: '15px',
                padding: '40px',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
              }}>
                <h3 className="text-center mb-4" style={{fontSize: '28px', fontWeight: 'bold', color: '#1C2938'}}>Key Target Markets</h3>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  {['Manufacturing', 'Professional Services', 'Retail', 'Distribution', 'Technology', 'Consulting', 'Healthcare', 'Education'].map((market, idx) => (
                    <div key={idx} style={{
                      background: 'white',
                      padding: '12px 24px',
                      borderRadius: '25px',
                      fontWeight: 600,
                      color: '#4A90E2',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s'
                    }}>
                      {market}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase */}
      <section className="py-130 rpt-100 rpb-100" style={{background: 'white'}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 text-center mb-60">
              <div className="section-title">
                <span className="sub-title" style={{color: '#4A90E2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px'}}>17 Powerful Features</span>
                <h2 className="mt-3 mb-4">Comprehensive CRM Features</h2>
                <p className="text-muted">Everything you need to manage your entire business</p>
              </div>
            </div>
          </div>

          <div className="row">
            {[
              { icon: 'fas fa-project-diagram', title: 'Project Management', color: '#4A90E2' },
              { icon: 'fas fa-file-invoice-dollar', title: 'Invoicing & Estimates', color: '#48BB78' },
              { icon: 'fas fa-file-alt', title: 'Proposals', color: '#4A90E2' },
              { icon: 'fas fa-users', title: 'Lead Management', color: '#48BB78' },
              { icon: 'fas fa-money-bill-wave', title: 'Expenses & Payments', color: '#4A90E2' },
              { icon: 'fas fa-headset', title: 'Support System', color: '#48BB78' },
              { icon: 'fas fa-file-signature', title: 'Contracts & E-Signatures', color: '#4A90E2' },
              { icon: 'fas fa-poll', title: 'Surveys', color: '#48BB78' },
              { icon: 'fas fa-bullseye', title: 'Goals Tracking', color: '#4A90E2' },
              { icon: 'fas fa-edit', title: 'Custom Fields', color: '#48BB78' },
              { icon: 'fas fa-chart-bar', title: 'Reporting & Analytics', color: '#4A90E2' },
              { icon: 'fas fa-newspaper', title: 'Company Newsfeed', color: '#48BB78' },
              { icon: 'fas fa-user-shield', title: 'User Management', color: '#4A90E2' },
              { icon: 'fas fa-lock', title: 'Security & Permissions', color: '#48BB78' },
              { icon: 'fas fa-sync-alt', title: 'Updates & Enhancements', color: '#4A90E2' },
              { icon: 'fas fa-cloud', title: 'SaaS Module', color: '#48BB78' },
              { icon: 'fas fa-tags', title: 'Licensing & Pricing', color: '#4A90E2' }
            ].map((feature, idx) => (
              <div key={idx} className="col-lg-3 col-md-4 col-sm-6 mb-4">
                <div className="text-center" style={{
                  background: 'white',
                  padding: '25px 20px',
                  borderRadius: '12px',
                  boxShadow: '0 3px 15px rgba(0,0,0,0.08)',
                  borderTop: `3px solid ${feature.color}`,
                  height: '100%',
                  transition: 'all 0.3s'
                }}>
                  <i className={feature.icon} style={{
                    fontSize: '32px',
                    color: feature.color,
                    marginBottom: '15px',
                    display: 'block'
                  }}></i>
                  <h5 className="mb-0" style={{fontSize: '14px', fontWeight: 600, color: '#1C2938'}}>{feature.title}</h5>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/features-detail" className="theme-btn style-three" style={{
              padding: '15px 40px',
              fontSize: '16px',
              fontWeight: 600
            }}>
              View All Features in Detail <i className="fas fa-arrow-right ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Traction & Testimonials */}
      <section className="py-130 rpt-100 rpb-100 bg-lighter" id="traction">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 text-center mb-60">
              <div className="section-title">
                <span className="sub-title" style={{color: '#4A90E2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px'}}>Success Stories</span>
                <h2 className="mt-3 mb-4">Customer Success Stories</h2>
                <p className="text-muted">Trusted by growing businesses across Malaysia</p>
              </div>
            </div>
          </div>

          <div className="row mb-5">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="col-lg-6 mb-4">
                <div className="feedback-item-two" style={{
                  background: 'white',
                  padding: '40px',
                  borderRadius: '15px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                  height: '100%',
                  borderLeft: '4px solid #4A90E2'
                }}>
                  <i className="flaticon-quote-1" style={{fontSize: '48px', color: '#4A90E2', marginBottom: '20px', opacity: 0.3}}></i>
                  <blockquote className="mb-4" style={{fontSize: '18px', lineHeight: '1.8', color: '#718096', fontStyle: 'italic'}}>
                    "{testimonial.quote}"
                  </blockquote>
                  <div className="feedback-author d-flex align-items-center">
                    <div className="author-content">
                      <h5 className="mb-1" style={{fontSize: '20px', fontWeight: 'bold', color: '#1C2938'}}>{testimonial.author}</h5>
                      <span style={{color: '#718096'}}>{testimonial.company}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row">
            <div className="col-12">
              <div style={{
                background: 'linear-gradient(135deg, #4A90E2 0%, #48BB78 100%)',
                borderRadius: '15px',
                padding: '50px',
                color: 'white',
                boxShadow: '0 10px 40px rgba(74, 144, 226, 0.3)'
              }}>
                <div className="row text-center">
                  {[
                    { number: '40%', desc: 'Increase in conversion rates', icon: 'fas fa-arrow-up' },
                    { number: '60%', desc: 'Time saved on reporting', icon: 'fas fa-clock' },
                    { number: '50%', desc: 'Cost reduction vs competitors', icon: 'fas fa-dollar-sign' },
                    { number: '100+', desc: 'Projects completed', icon: 'fas fa-check-circle' }
                  ].map((stat, idx) => (
                    <div key={idx} className="col-lg-3 col-md-6 mb-4 mb-lg-0">
                      <i className={stat.icon} style={{fontSize: '36px', marginBottom: '15px', opacity: 0.9}}></i>
                      <div className="mb-2" style={{fontSize: '48px', fontWeight: 'bold'}}>{stat.number}</div>
                      <p className="mb-0" style={{fontSize: '16px', opacity: 0.9}}>{stat.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-130 rpt-100 rpb-100" style={{background: 'white'}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 text-center mb-60">
              <div className="section-title">
                <span className="sub-title" style={{color: '#4A90E2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px'}}>Pricing</span>
                <h2 className="mt-3 mb-4">Cost-Effective One-Time Purchase</h2>
                <p className="text-muted" style={{fontSize: '18px'}}>No monthly subscription fees. Own your CRM forever.</p>
              </div>
            </div>
          </div>

          {loadingPricing ? (
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                <p style={{fontSize: '18px', color: '#718096'}}>Loading pricing plans...</p>
              </div>
            </div>
          ) : pricings.length === 0 ? (
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                <p style={{fontSize: '18px', color: '#718096'}}>No pricing plans available at the moment.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="row justify-content-center align-items-stretch">
                {pricings.map((pricing, index) => {
                  const isPopular = index === 1 || (pricings.length > 0 && index === Math.floor(pricings.length / 2));
                  const isGradient = isPopular;
                  
                  // Calculate column width based on number of pricing plans
                  const getColumnClass = () => {
                    if (pricings.length === 1) return 'col-lg-6 col-md-6';
                    if (pricings.length === 2) return 'col-lg-5 col-md-6';
                    return 'col-lg-4 col-md-6';
                  };
                  
                  return (
                    <div key={pricing.id} className={`${getColumnClass()} mb-4 d-flex`}>
                      <div className="pricing-item w-100" style={{
                        background: isGradient 
                          ? 'linear-gradient(135deg, #4A90E2 0%, #48BB78 100%)' 
                          : 'white',
                        padding: '40px 30px',
                        borderRadius: '15px',
                        boxShadow: isGradient 
                          ? '0 10px 40px rgba(74, 144, 226, 0.3)' 
                          : '0 5px 20px rgba(0,0,0,0.08)',
                        border: isGradient ? 'none' : '2px solid #e2e8f0',
                        color: isGradient ? 'white' : 'inherit',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        {isPopular && (
                          <div style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            background: 'rgba(255, 255, 255, 0.25)',
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase'
                          }}>POPULAR</div>
                        )}
                        <h5 className="price-title mb-4" style={{
                          fontSize: '26px', 
                          fontWeight: 'bold', 
                          color: isGradient ? 'white' : '#1C2938',
                          marginTop: '10px',
                          marginBottom: '20px'
                        }}>
                          {pricing.title}
                        </h5>
                        <div className="mb-3" style={{minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <span className="price" style={{
                            fontSize: 'clamp(32px, 4vw, 42px)', 
                            fontWeight: 'bold', 
                            color: isGradient ? 'white' : '#4A90E2',
                            lineHeight: '1.2',
                            wordBreak: 'break-word'
                          }}>
                            {pricing.price}
                          </span>
                        </div>
                        {pricing.user_range && (
                          <p className="mb-4" style={{
                            color: isGradient ? 'rgba(255,255,255,0.95)' : '#718096',
                            fontSize: '15px',
                            fontWeight: 500,
                            minHeight: '24px'
                          }}>
                            {pricing.user_range}
                          </p>
                        )}
                        <div className="mb-4" style={{flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start'}}>
                          <Link 
                            to="/pricing" 
                            className="theme-btn" 
                            style={{
                              display: 'inline-block',
                              background: isGradient ? 'white' : 'linear-gradient(135deg, #4A90E2 0%, #48BB78 100%)',
                              color: isGradient ? '#4A90E2' : 'white',
                              padding: '14px 32px',
                              fontSize: '15px',
                              fontWeight: 600,
                              textDecoration: 'none',
                              borderRadius: '8px',
                              transition: 'all 0.3s',
                              width: '100%',
                              textAlign: 'center',
                              marginBottom: '30px'
                            }}
                          >
                            Choose Package <i className="fas fa-arrow-right ml-2"></i>
                          </Link>
                          {pricing.features && pricing.features.length > 0 && (
                            <ul className="list-style-one text-left" style={{
                              color: isGradient ? 'white' : '#4A5568',
                              paddingLeft: '0',
                              listStyle: 'none',
                              margin: '0',
                              textAlign: 'left'
                            }}>
                              {pricing.features.map((feature, featureIndex) => (
                                <li key={featureIndex} style={{
                                  marginBottom: '12px',
                                  fontSize: '15px',
                                  lineHeight: '1.6',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  paddingLeft: '24px',
                                  position: 'relative'
                                }}>
                                  <i className="fas fa-check-circle" style={{
                                    position: 'absolute',
                                    left: '0',
                                    top: '4px',
                                    color: isGradient ? 'white' : '#4A90E2',
                                    fontSize: '16px'
                                  }}></i>
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="row mt-5">
                <div className="col-12">
                  <div className="text-center" style={{
                    background: '#f8f9fa',
                    padding: '30px',
                    borderRadius: '15px'
                  }}>
                    <p className="mb-0" style={{fontSize: '16px', color: '#718096'}}>
                      <i className="fas fa-info-circle mr-2" style={{color: '#4A90E2'}}></i>
                      <strong>Note:</strong> Supports unlimited users depending on server capacity. All licenses include lifetime updates and access to all 17 features.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-130 rpt-100 rpb-100 text-white" id="contact" style={{
        background: 'linear-gradient(135deg, #4A90E2 0%, #48BB78 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 text-center">
              <h2 className="mb-4" style={{
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                fontWeight: 'bold',
                lineHeight: '1.2',
                marginBottom: '25px'
              }}>
                Empower Your Business Growth
              </h2>
              <p className="mb-5 mx-auto" style={{
                fontSize: 'clamp(16px, 2vw, 20px)',
                opacity: 0.95,
                lineHeight: '1.6',
                maxWidth: '800px',
                marginBottom: '40px'
              }}>
                Join Malaysian SMEs that are doubling their sales, reducing CRM costs by 50%, and scaling smarter with Acuit CRM
              </p>
              <div className="d-flex flex-wrap align-items-center justify-content-center gap-3 mb-5" style={{
                gap: '20px',
                marginBottom: '50px'
              }}>
                <Link to="/contact" className="theme-btn style-two" style={{
                  background: 'white',
                  color: '#4A90E2',
                  padding: '15px 40px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}>
                  Schedule a Demo <i className="fas fa-arrow-right ml-2"></i>
                </Link>
                <Link to="/become-partner" className="theme-btn style-three" style={{
                  background: 'transparent',
                  color: 'white',
                  border: '2px solid white',
                  padding: '15px 40px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap'
                }}>
                  Become a Partner <i className="fas fa-arrow-right ml-2"></i>
                </Link>
              </div>
              
              <div className="border-top border-white border-opacity-20 pt-4 mt-5">
                <p className="mb-3" style={{fontSize: '14px', opacity: 0.9}}>Contact Us</p>
                <div className="d-flex flex-wrap justify-content-center align-items-center gap-3" style={{fontSize: '14px'}}>
                  <a href="mailto:mail@plenitudeit.com.my" style={{color: 'white', textDecoration: 'none', opacity: 0.9}}>
                    <i className="fas fa-envelope mr-2"></i>mail@plenitudeit.com.my
                  </a>
                  <span className="d-none d-sm-inline">•</span>
                  <Link to="/" style={{color: 'white', textDecoration: 'none', opacity: 0.9}}>
                    <i className="fas fa-globe mr-2"></i>acuit.com.my
                  </Link>
                  <span className="d-none d-sm-inline">•</span>
                  <a href="tel:+60327271889" style={{color: 'white', textDecoration: 'none', opacity: 0.9}}>
                    <i className="fas fa-phone mr-2"></i>+603-2727 1889
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <img className="dots-shape" src="/assets/images/shapes/dots.png" alt="Shape" style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          opacity: 0.1
        }} />
        <img className="tringle-shape" src="/assets/images/shapes/tringle.png" alt="Shape" style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          opacity: 0.1
        }} />
      </section>

      <style>{`
        .pitch-tech-page .service-item:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.15) !important;
        }
        .pitch-tech-page .solution-item-three:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.15) !important;
        }
        .pitch-tech-page a[href^="#"] {
          cursor: pointer;
        }
        .pitch-tech-header .navigation a:hover {
          opacity: 0.8;
          transition: opacity 0.3s;
        }
        .hero-content-pitch .theme-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.3) !important;
        }
        .hero-content-pitch .theme-btn.style-three:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .pitch-tech-page .card .text-center:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
          border-color: #4A90E2 !important;
        }
        @media (max-width: 576px) {
          .hero-content-pitch .d-flex {
            flex-direction: column;
            width: 100%;
          }
          .hero-content-pitch .theme-btn {
            width: 100%;
            justify-content: center;
          }
          #contact .d-flex.flex-wrap {
            flex-direction: column;
            width: 100%;
          }
          #contact .theme-btn {
            width: 100%;
            max-width: 100%;
          }
        }
        .pitch-tech-header {
          transition: all 0.3s ease;
        }
        .pitch-tech-header:hover {
          background: rgba(28, 41, 56, 0.2) !important;
        }
        @media (max-width: 768px) {
          .pitch-tech-page .hero-pitch {
            min-height: auto;
            padding: 100px 0 60px 0;
          }
          .pitch-tech-header {
            position: relative !important;
            background: rgba(28, 41, 56, 0.95) !important;
          }
        }
      `}</style>
    </div>
  );
}

export default PitchTech;
